import React, { PropsWithChildren, useCallback, useMemo, useRef, useState } from 'react';
import { FieldPath, FieldValues, useController, UseControllerProps } from 'react-hook-form';
import { clsx } from 'clsx';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isEmptyString, isNotEmptyArray, isNotEmptyString, isNotSet, isSet, isString } from '@/utils';
import useInteractiveOutsideHandler from '@/hooks/useInteractiveOutsideHandler';
import ConditionalFragment from '@/components/common/ConditionalFragment';
import Flexbox from '@/components/common/Flexbox';
import TailingModal from '@/components/feature/TailingModal';
export interface RenderDisplayProps<IsMulti extends boolean> {
  displayValue: string | undefined | null | React.ReactElement;
  hasSelected: boolean;
  selectedKeys?: IsMulti extends true ? Array<string | number> : string | number;
}
interface SelectComponent extends CustomizeFunctionComponent {
  <TFormValues extends FieldValues, Name extends FieldPath<TFormValues>, IsMulti extends boolean>(props: PropsWithChildren<{
    chevronClassName?: string;
    className?: string;
    options: Array<Option<TFormValues[Name]> & {
      leftSlot?: React.ReactElement;
    }>;
    placeholder?: string;
    isMultiple?: IsMulti;
    renderDisplay?: React.FC<RenderDisplayProps<IsMulti>>;
    disableBolder?: boolean;
    disablePadding?: boolean;
    wrapperRef?: HTMLDivElement | null;
  } & UseControllerProps<TFormValues, Name>>, context?: any): React.ReactElement | null;
}
export const Select: SelectComponent = ({
  className,
  chevronClassName,
  name,
  control,
  placeholder,
  options,
  rules,
  isMultiple = false,
  renderDisplay,
  disableBolder,
  disablePadding,
  wrapperRef: specificWrapperRef = null
}) => {
  const [isOptionOpen, setIsOptionOpen] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement | null>(specificWrapperRef);
  const optionRef = useRef<HTMLDivElement | null>(null);
  const {
    field: {
      value,
      onChange
    },
    fieldState: {
      error
    }
  } = useController({
    control,
    name,
    rules
  });
  const toggleOptions = useCallback(() => {
    setIsOptionOpen(prev => !prev);
  }, []);
  useInteractiveOutsideHandler({
    element: optionRef.current,
    callback: () => setIsOptionOpen(false),
    excludeElements: [wrapperRef.current]
  });
  const onItemClick = useCallback<React.MouseEventHandler<HTMLDivElement>>(event => {
    const key = event.currentTarget.dataset.key;
    if (!key) return;
    if (isMultiple) {
      let result = (value ?? []) as Array<string | number>;
      onChange(result.includes(key) ? result.filter(existKey => existKey !== key) : result.concat(key));
    } else {
      onChange(key);
      toggleOptions();
    }
  }, [isMultiple, value]);
  const displayValue = useMemo(() => {
    if (!isMultiple) {
      const option = options.find(option => option.key === value);
      return <React.Fragment>
          {option?.leftSlot}
          {option?.label ?? placeholder}
        </React.Fragment>;
    } else {
      const result = options.filter(({
        key
      }) => (value as Array<string | number> ?? []).includes(key))?.map(({
        label
      }) => label).join('、');
      return isEmptyString(result) ? placeholder : result;
    }
    // eslint-disable-next-line
  }, [value, isMultiple, options]);
  const bindRef = useCallback((ref: HTMLDivElement) => {
    if (specificWrapperRef) wrapperRef.current = specificWrapperRef;else wrapperRef.current = ref;
  }, [specificWrapperRef]);
  return <React.Fragment>
      <TailingModal isOpen={isOptionOpen} wrapperRef={wrapperRef} customRef={optionRef} fitWrapperWidth className={'overflow-scroll bg-white'} data-sentry-element="TailingModal" data-sentry-source-file="Select.tsx">
        {options.map(({
        label,
        key,
        leftSlot
      }) => <Flexbox key={key} align={'center'} className={clsx('px-4 h-12 text-fz5', 'bg-white hoverable:hover:bg-blue/[.15] active:bg-blue/[.15]', {
        'bg-blue/[.15]': isMultiple ? (value as Array<string | number>)?.includes(key) : value === key
      })} onClick={onItemClick} data-key={key}>
            {leftSlot}
            {label}
          </Flexbox>)}
      </TailingModal>
      <Flexbox ref={bindRef} align={'center'} justify={'between'} onClick={toggleOptions} className={clsx(className, 'relative', 'w-full', 'text-fz5 text-grey-2', 'bg-white', 'hover:cursor-pointer', {
      'border border-solid border-red': isSet(error),
      'border border-solid border-grey5 focus-within:border-blue focus-within:shadow-[0px_0px_0px_3px_rgba(89,_123,_212,_0.10)]': !disableBolder && isNotSet(error)
    }, {
      'p-3.5': !disablePadding
    }, {
      'rounded-m': !disableBolder
    })} data-sentry-element="Flexbox" data-sentry-source-file="Select.tsx">
        <ConditionalFragment condition={!renderDisplay} data-sentry-element="ConditionalFragment" data-sentry-source-file="Select.tsx">
          <Flexbox align={'center'} className={clsx('mr-2', 'text-base flex-1 truncate', {
          'text-grey-2': isNotSet(value) || isEmptyString(value)
        })} data-sentry-element="Flexbox" data-sentry-source-file="Select.tsx">
            {displayValue || placeholder}
          </Flexbox>
        </ConditionalFragment>
        <ConditionalFragment condition={isSet(renderDisplay)} data-sentry-element="ConditionalFragment" data-sentry-source-file="Select.tsx">
          {renderDisplay && React.createElement(renderDisplay, {
          displayValue: displayValue || placeholder,
          hasSelected: isSet(value) && (isString(value) && isNotEmptyString(value) || Array.isArray(value) && isNotEmptyArray(value)),
          // @ts-ignore
          selectedKeys: isMultiple ? value as Array<string | number> : value as string | number
        })}
        </ConditionalFragment>
        <span className={clsx(chevronClassName)}>
          <FontAwesomeIcon icon={faChevronDown} className={clsx('transform transition-transform', {
          'rotate-180': isOptionOpen
        })} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="Select.tsx" />
        </span>
      </Flexbox>
    </React.Fragment>;
};